import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  MailCheck,
  Shield,
  RefreshCw,
  Lock,
  HardDrive,
  Zap,
  Settings,
  Scale,
  CheckCircle2,
  XCircle,
  Smartphone,
} from "lucide-react";
import { motion } from "framer-motion";
import exchangeHeroBg from "@/assets/exchange-online-hero-bg.jpg";
import outlookIcon from "@/assets/ms-icons/outlook.svg";

const comparisonItems = [
  {
    title: "Volledige synchronisatie",
    exchange: "Inbox, verzonden items, concepten, agenda, contacten, taken — alles staat centraal en identiek op elk apparaat.",
    hosting: "POP downloadt alleen de Inbox en verwijdert vaak berichten van de server. IMAP synchroniseert alleen e-mail, géén agenda of contacten.",
    icon: RefreshCw,
    gradient: "bg-hero-gradient",
  },
  {
    title: "Nooit meer mail kwijt",
    exchange: "Alle e-mail staat server-side. Met automatische back-ups en retention herstel je alles vanuit het Microsoft-datacenter.",
    hosting: "Bij POP kan mail van de server verdwijnen zodra één apparaat het downloadt. Verzonden items staan alleen lokaal. Crasht dat apparaat? Alles weg.",
    icon: Shield,
    gradient: "bg-rinqel-blue-gradient",
  },
  {
    title: "Moderne beveiliging met MFA",
    exchange: "Volledige ondersteuning voor modern authentication (OAuth), MFA en Conditional Access. Jouw mailbox is écht beveiligd.",
    hosting: "POP/IMAP werken met basic authentication: één wachtwoord, geen MFA, geen Conditional Access. Eén gelekt wachtwoord = gehackt.",
    icon: Lock,
    gradient: "bg-rinqel-green-gradient",
  },
  {
    title: "Geavanceerde spam- en phishingbescherming",
    exchange: "Spoofing-bescherming, DKIM/DMARC, anti-phishing policies, anti-malware en AI-gestuurde beveiliging — standaard inbegrepen.",
    hosting: "Meestal alleen een basis spamfilter en een simpele virusscanner. Geen bescherming tegen spoofing of gerichte phishing.",
    icon: Shield,
    gradient: "bg-hero-gradient",
  },
  {
    title: "50 GB opslag + archivering",
    exchange: "50 GB mailbox standaard, plus 50 GB archive mailbox. Nooit meer opruimen of verwijderen om ruimte te maken.",
    hosting: "Gemiddeld 1–5 GB. Geen automatische archivering, geen retention policies. Mailbox zit snel vol.",
    icon: HardDrive,
    gradient: "bg-rinqel-blue-gradient",
  },
  {
    title: "Snelle, directe verbinding",
    exchange: "MAPI/EAS zorgt voor een directe, realtime verbinding. Geen vertraging, geen omwegen.",
    hosting: "POP/IMAP via proxy introduceert wachttijd. Microsoft bevestigt: \"gebruikers ervaren vertragingen van enkele seconden.\"",
    icon: Zap,
    gradient: "bg-rinqel-green-gradient",
  },
  {
    title: "Centraal beheer en controle",
    exchange: "Mobile Device Management, remote wipe, app-protection policies, DLP en geautomatiseerde back-ups — allemaal inbegrepen.",
    hosting: "Geen beheer, geen policies, geen controle. Je kunt niet eens zien wie er toegang heeft tot welke mailbox.",
    icon: Settings,
    gradient: "bg-hero-gradient",
  },
  {
    title: "Compliance en juridische zekerheid",
    exchange: "Server-side eDiscovery, Legal Hold, audit trail en compliance policies. Geschikt voor elke organisatie met juridische verplichtingen.",
    hosting: "Geen logging, geen audit trail, geen legal hold. Je weet niet eens waar e-mail staat — elk apparaat heeft een eigen kopie.",
    icon: Scale,
    gradient: "bg-rinqel-blue-gradient",
  },
];

const summaryExchange = [
  "Centraal beheer",
  "Volledige synchronisatie",
  "Veiligheid met MFA / OAuth",
  "50 GB mailbox + archivering",
  "Back-up & herstel",
  "Compliance & legal hold",
  "Geavanceerde beveiliging",
  "Professioneel beheer door Rinqel",
];

const summaryHosting = [
  "Alleen basis e-mail",
  "Geen synchronisatie buiten Inbox",
  "Geen kalender of contacten",
  "Geen MFA mogelijk",
  "Geen compliance",
  "Geen centraal beheer",
  "Mail verspreid over apparaten",
  "Groot risico op verlies",
];

const WerkplekExchangeOnline = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img loading="eager" decoding="async" fetchPriority="high" src={exchangeHeroBg} alt="Exchange Online: zakelijke e-mail met Microsoft 365" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(210,25%,12%)]/90 via-[hsl(210,30%,18%)]/80 to-[hsl(204,60%,30%)]/70" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_60%)]" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Waarom niet gewoon POP/IMAP? Lees waarom.
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <img loading="lazy" decoding="async" src={outlookIcon} alt="Outlook" className="h-8 w-8 object-contain" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Microsoft Exchange{" "}
              <span className="text-gradient">Online</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Zakelijke e-mail zoals het hoort. Volledig gesynchroniseerd, veilig met MFA en altijd een back-up. Waarom Exchange Online de betere keuze is ten opzichte van POP/IMAP bij hostingproviders.
            </motion.p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="text-4xl font-bold text-white">€7</span>
              <span className="ml-1 text-white/70">/per maand per mailbox</span>
            </motion.div>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                50 GB per mailbox
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                Inclusief back-up
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                MFA & moderne beveiliging
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Waarom Exchange Online beter is dan POP/IMAP
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Veel bedrijven gebruiken nog e-mail via hun hostingprovider met POP of IMAP. Dat werkte prima in 2005 — maar in 2025 loop je er flinke risico's mee. Hieronder leggen we uit waarom.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Inbegrepen bij */}
      <section className="pb-16 md:pb-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl rounded-xl border border-border bg-card shadow-card overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-rinqel-blue-gradient px-6 py-4 flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-base font-bold text-white leading-tight">Al inbegrepen bij je werkplek</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Goed om te weten: een Exchange Online mailbox van 50 GB is standaard inbegrepen bij de volgende werkplekken. Je hoeft deze dus niet los af te nemen als je al een van deze pakketten hebt:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-rinqel-green" />
                  <span>
                    <Link to="/diensten/werkplek/cloud-only" className="font-semibold text-primary hover:underline">Managed Cloud Only Werkplek</Link>
                    <span className="text-muted-foreground"> — 50 GB mailbox + archivering</span>
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-rinqel-green" />
                  <span>
                    <Link to="/diensten/werkplek/managed" className="font-semibold text-primary hover:underline">Managed Werkplek</Link>
                    <span className="text-muted-foreground"> — 50 GB mailbox + archivering</span>
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-rinqel-green" />
                  <span>
                    <Link to="/diensten/werkplek/kiosk" className="font-semibold text-primary hover:underline">Managed Kiosk Werkplek</Link>
                    <span className="text-muted-foreground"> — 50 GB mailbox + archivering</span>
                  </span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-rinqel-green" />
                  <span>
                    <Link to="/diensten/werkplek/mobile-device" className="font-semibold text-primary hover:underline">Managed Mobile Device Only</Link>
                    <span className="text-muted-foreground"> — 2 GB mailbox, zonder archief (werkt verder hetzelfde)</span>
                  </span>
                </li>
              </ul>
              <p className="text-sm leading-relaxed text-muted-foreground border-t border-border pt-4">
                De Losse Mailbox van €7 per maand is bedoeld voor medewerkers die géén werkplek nodig hebben, maar wél professioneel willen mailen.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison cards */}
      <section className="pb-16 md:pb-20">
        <div className="container">
          <div className="grid gap-6 md:grid-cols-2">
            {comparisonItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex flex-col rounded-xl border border-border bg-card shadow-card overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <div className={`flex items-center gap-3 px-6 py-4 ${item.gradient}`}>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-white leading-tight">{item.title}</h3>
                  </div>
                  <div className="flex flex-1 flex-col gap-4 p-6">
                    <div className="flex gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-rinqel-green mb-1">Exchange Online</p>
                        <p className="text-sm leading-relaxed text-muted-foreground">{item.exchange}</p>
                      </div>
                    </div>
                    <div className="border-t border-border pt-4 flex gap-3">
                      <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-destructive mb-1">POP/IMAP (hostingprovider)</p>
                        <p className="text-sm leading-relaxed text-muted-foreground">{item.hosting}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              De vergelijking op een rij
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              E-mail via POP/IMAP is functioneel achterhaald en zakelijk risicovol. Exchange Online is veilig, betrouwbaar en professioneel.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Exchange column */}
            <motion.div
              className="rounded-xl border border-border bg-card shadow-card overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-rinqel-green-gradient px-6 py-4 flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-bold text-white">Exchange Online via Rinqel</h3>
              </div>
              <ul className="p-6 space-y-3">
                {summaryExchange.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-rinqel-green" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Hosting column */}
            <motion.div
              className="rounded-xl border border-border bg-card shadow-card overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-muted px-6 py-4 flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10">
                  <XCircle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="text-base font-bold">Hostingprovider (POP/IMAP)</h3>
              </div>
              <ul className="p-6 space-y-3">
                {summaryHosting.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Rinqel adds */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Wat Rinqel daar bovenop doet
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Je krijgt niet zomaar een licentie. Wij zorgen dat alles professioneel is ingericht, beveiligd en beheerd.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Settings, title: "Professionele inrichting", text: "Jouw mailbox wordt correct geconfigureerd met SPF, DKIM en DMARC. Geen gedoe, gewoon goed.", gradient: "bg-hero-gradient" },
              { icon: Shield, title: "Beveiliging", text: "MFA, anti-phishing, encryptie en mailflow rules — standaard inbegrepen en door ons beheerd.", gradient: "bg-rinqel-blue-gradient" },
              { icon: HardDrive, title: "Online & offsite back-up", text: "Wij maken doorlopend back-ups van je mailbox. Onafhankelijk van Microsoft. Altijd inbegrepen.", gradient: "bg-rinqel-green-gradient" },
              { icon: Smartphone, title: "Monitoring & beheer", text: "Wij houden je mailomgeving in de gaten en grijpen proactief in bij problemen of bedreigingen.", gradient: "bg-hero-gradient" },
              { icon: Mail, title: "Ondersteuning", text: "Vragen? Problemen? Ons team staat voor je klaar — persoonlijk en zonder wachtrij.", gradient: "bg-rinqel-blue-gradient" },
              { icon: RefreshCw, title: "Migratie inbegrepen", text: "We migreren je bestaande mailboxen gratis naar Exchange Online. Zonder downtime, zonder stress.", gradient: "bg-rinqel-green-gradient" },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex flex-col rounded-xl border border-border bg-card shadow-card overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <div className={`flex items-center gap-3 px-6 py-4 ${item.gradient}`}>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-white leading-tight">{item.title}</h3>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <Link
            to="/praktijkvoorbeelden/veilig-mailen-zorg"
            className="group mx-auto mt-10 flex max-w-3xl items-center justify-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-6 py-4 text-center font-semibold text-foreground transition-colors hover:border-primary hover:bg-primary/10"
          >
            <MailCheck className="h-5 w-5 shrink-0 text-primary" />
            <span>Werk je in de zorg? Zo mail je veilig vanaf hetzelfde adres.</span>
            <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-hero-gradient-subtle">
        <div className="container">
          <motion.div
            className="mx-auto max-w-2xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Klaar om over te stappen?
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wij regelen de migratie, inrichting en beveiliging. Jij hoeft alleen maar te mailen.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button variant="hero" size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="werkplek-exchange-online-cta">
                  Neem contact op
                  <ArrowRight className="ml-2 h-4 w-4" />
                </TrackedLink>
              </Button>
              <Button variant="hero-outline" size="lg" asChild>
                <Link to="/diensten/werkplek">Terug naar overzicht</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WerkplekExchangeOnline;
